/**
 * Record updates, arranged: each housekeeping suggestion as "field: before →
 * after", grouped by what it changes (the company profile, then each
 * document), with three or more additions to one list (control links) folded
 * into a bundle. Pure, so the arrangement and wording are pinned by tests.
 *
 * Before-values: an applied update shows the value its Apply replaced (kept
 * by the API, `appliedRecord`); a pending one shows the value the record
 * holds now, read from the same lists the library already loads.
 *
 * @module components/regulation/recordUpdates/recordUpdates.logic
 */
import { isRecordUpdateKind } from "@repo/shared/findingPlanning";
import { formatRecordValue, normaliseList, type RecordValue } from "@repo/shared/recordUpdates";
import { isOpenSuggestionStatus } from "@repo/shared/reviewCounts";

import type { Recommendation } from "../../../state/governance";

export type RecordStatus = "pending" | "applied" | "skipped";
export type RecordTab = RecordStatus | "all";

/** What the library knows about a document, for before-values and names. */
export interface RecordSources {
  readonly documents: ReadonlyMap<
    string,
    {
      readonly name: string;
      readonly title: string | null;
      readonly documentKind: string;
      readonly jurisdictionSlugs: ReadonlyArray<string>;
      readonly governanceFrameworkId: string | null;
    }
  >;
  readonly governance: ReadonlyMap<string, { readonly title: string | null; readonly department: string | null; readonly reviewFrequency: string }>;
  readonly ownerEmails: ReadonlyMap<string, ReadonlyArray<string>>;
  /** Framework names by id. */
  readonly frameworks: ReadonlyMap<string, string>;
  /** Jurisdiction names by slug. */
  readonly jurisdictions: ReadonlyMap<string, string>;
  /** People's names by email, lower-cased. */
  readonly people: ReadonlyMap<string, string>;
  readonly organizationContext: string | null | undefined;
}

export interface RecordUpdate {
  readonly id: string;
  readonly kind: Recommendation["kind"];
  /** "Title", "Review cadence", "Control link"… */
  readonly label: string;
  readonly status: RecordStatus;
  /** "org" for the company profile; the document id otherwise. */
  readonly target: string;
  /** The document's name, or "Company profile". */
  readonly targetName: string;
  /** "set" replaces a value; "add" / "remove" change one item of a list. */
  readonly op: "set" | "add" | "remove";
  /** The before-value as text; null when unknown (a legacy apply). */
  readonly before: string | null;
  readonly after: string;
  /** For the company profile: the words added. */
  readonly addition: string | null;
  readonly why: string;
  /** Applied before before-values were kept: nothing to restore. */
  readonly legacy: boolean;
  readonly suggestion: Recommendation;
}

export const RECORD_LABELS: Readonly<Partial<Record<Recommendation["kind"], string>>> = {
  rename: "Title",
  review_cadence: "Review cadence",
  owner: "Owners",
  department: "Department",
  jurisdictions: "Jurisdictions",
  reclassify: "Shelf",
  control_mapping: "Control link",
  framework: "Framework",
  org_context: "Company profile",
};

export const recordStatusOf = (status: Recommendation["status"]): RecordStatus =>
  isOpenSuggestionStatus(status) ? "pending" : status === "applied" ? "applied" : "skipped";

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value.trim() : null);
const list = (value: unknown): ReadonlyArray<string> =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];

/** The record's current value, in the API's normalised form. Undefined when unknown. */
export function currentRecordValue(suggestion: Recommendation, sources: RecordSources): RecordValue | undefined {
  if (suggestion.kind === "org_context") return sources.organizationContext === undefined ? undefined : (sources.organizationContext ?? null);
  const policyId = suggestion.policyId;
  if (policyId === null) return undefined;
  const document = sources.documents.get(policyId);
  if (!document) return undefined;
  const governance = sources.governance.get(policyId);
  switch (suggestion.kind) {
    case "rename":
      return governance?.title ?? document.title ?? null;
    case "review_cadence":
      return governance?.reviewFrequency ?? "none";
    case "department":
      return governance?.department ?? null;
    case "owner":
      return normaliseList(sources.ownerEmails.get(policyId) ?? [], true);
    case "jurisdictions":
      return normaliseList(document.jurisdictionSlugs);
    case "reclassify":
      return [document.documentKind];
    case "framework":
      return document.governanceFrameworkId;
    default:
      return undefined;
  }
}

/** A value as the reader knows it: names, not ids. */
export function describeRecordValue(kind: Recommendation["kind"], value: RecordValue | undefined, sources: RecordSources): string {
  if (value === undefined) return "Unknown";
  if (kind === "framework" && typeof value === "string") return sources.frameworks.get(value) ?? "A framework";
  if (kind === "jurisdictions" && Array.isArray(value)) {
    return value.length === 0 ? "None" : value.map((slug) => sources.jurisdictions.get(slug) ?? slug).join(", ");
  }
  if (kind === "owner" && Array.isArray(value)) {
    return value.length === 0 ? "None" : value.map((email) => sources.people.get(email.toLowerCase()) ?? email).join(", ");
  }
  return formatRecordValue(kind, value);
}

/** The value Apply will write, from the proposal. */
function proposedValue(suggestion: Recommendation, sources: RecordSources): string {
  const change = suggestion.proposedChange as Record<string, unknown>;
  switch (suggestion.kind) {
    case "rename":
      return text(change.title) ?? "Not set";
    case "review_cadence":
      return formatRecordValue("review_cadence", text(change.reviewFrequency));
    case "department":
      return text(change.department) ?? "Not set";
    case "owner":
      return describeRecordValue("owner", normaliseList(list(change.ownerEmails), true), sources);
    case "jurisdictions":
      return describeRecordValue("jurisdictions", normaliseList(list(change.jurisdictionSlugs)), sources);
    case "reclassify":
      return change.documentKind === "procedure" ? "Procedure" : "Policy";
    case "framework":
      return text(change.frameworkName) ?? (text(change.frameworkId) ? (sources.frameworks.get(String(change.frameworkId)) ?? "A framework") : "No framework");
    case "control_mapping":
      return text(change.controlCode) ?? "A control";
    case "org_context":
      return text(change.contextAddition) ?? "";
    default:
      return "";
  }
}

/** One record update, ready to draw. Null for a gap suggestion. */
export function toRecordUpdate(suggestion: Recommendation, sources: RecordSources): RecordUpdate | null {
  if (!isRecordUpdateKind(suggestion.kind)) return null;
  const status = recordStatusOf(suggestion.status);
  const change = suggestion.proposedChange as Record<string, unknown>;
  const op: RecordUpdate["op"] =
    suggestion.kind === "control_mapping" ? (change.controlAction === "unlink" ? "remove" : "add") : "set";
  const applied = suggestion.appliedRecord ?? null;
  const beforeValue = op !== "set" ? undefined : status === "applied" ? (applied ? applied.before : undefined) : currentRecordValue(suggestion, sources);
  // An untitled document shows its file name; say that rather than "Not set".
  const before =
    beforeValue === undefined
      ? null
      : suggestion.kind === "rename" && beforeValue === null && suggestion.policyId
        ? (sources.documents.get(suggestion.policyId)?.name ?? "Not set")
        : describeRecordValue(suggestion.kind, beforeValue, sources);
  const after =
    status === "applied" && applied && op === "set" && suggestion.kind !== "org_context"
      ? describeRecordValue(suggestion.kind, applied.after, sources)
      : proposedValue(suggestion, sources);
  return {
    id: suggestion.id,
    kind: suggestion.kind,
    label: RECORD_LABELS[suggestion.kind] ?? suggestion.kind,
    status,
    target: suggestion.kind === "org_context" || suggestion.policyId === null ? "org" : suggestion.policyId,
    targetName:
      suggestion.kind === "org_context" || suggestion.policyId === null
        ? "Company profile"
        : (sources.documents.get(suggestion.policyId)?.name ?? suggestion.policyName ?? "Document"),
    op,
    before,
    after,
    addition: suggestion.kind === "org_context" ? text(change.contextAddition) : null,
    why: suggestion.rationale || suggestion.title,
    legacy: status === "applied" && applied === null,
    suggestion,
  };
}

export type RecordItem =
  | { readonly kind: "one"; readonly update: RecordUpdate }
  | { readonly kind: "bundle"; readonly key: string; readonly label: string; readonly updates: ReadonlyArray<RecordUpdate> };

/** Three or more additions (or removals) of one kind on one target fold into one item, at the first one's place. */
export function bundleRecordUpdates(updates: ReadonlyArray<RecordUpdate>): ReadonlyArray<RecordItem> {
  const keyOf = (update: RecordUpdate) => (update.op === "set" ? null : `${update.target}:${update.kind}:${update.op}`);
  const counts = new Map<string, number>();
  for (const update of updates) {
    const key = keyOf(update);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const items: Array<RecordItem> = [];
  const bundles = new Map<string, { kind: "bundle"; key: string; label: string; updates: RecordUpdate[] }>();
  for (const update of updates) {
    const key = keyOf(update);
    if (!key || (counts.get(key) ?? 0) < 3) {
      items.push({ kind: "one", update });
      continue;
    }
    let bundle = bundles.get(key);
    if (!bundle) {
      bundle = { kind: "bundle", key, label: update.op === "remove" ? `${update.label} removals` : `${update.label}s`, updates: [] };
      bundles.set(key, bundle);
      items.push(bundle);
    }
    bundle.updates.push(update);
  }
  return items;
}

export interface RecordGroup {
  readonly key: string;
  readonly title: string;
  readonly policyId: string | null;
  readonly updates: ReadonlyArray<RecordUpdate>;
}

/** The company profile first, then documents by name; within a group, the order the API gave (newest first). */
export function groupRecordUpdates(updates: ReadonlyArray<RecordUpdate>): RecordGroup[] {
  const groups = new Map<string, RecordUpdate[]>();
  for (const update of updates) groups.set(update.target, [...(groups.get(update.target) ?? []), update]);
  return [...groups.entries()]
    .map(([key, groupUpdates]) => ({
      key,
      title: groupUpdates[0]?.targetName ?? "Document",
      policyId: key === "org" ? null : key,
      updates: groupUpdates,
    }))
    .sort((left, right) => (left.key === "org" ? -1 : right.key === "org" ? 1 : left.title.localeCompare(right.title)));
}

export const matchesRecordTab = (update: Pick<RecordUpdate, "status">, tab: RecordTab) => tab === "all" || update.status === tab;

export function recordCounts(updates: ReadonlyArray<RecordUpdate>): Readonly<Record<RecordTab, number>> {
  return {
    pending: updates.filter((update) => update.status === "pending").length,
    applied: updates.filter((update) => update.status === "applied").length,
    skipped: updates.filter((update) => update.status === "skipped").length,
    all: updates.length,
  };
}

/** The word count a profile addition adds, for its chip. */
export const addedWords = (addition: string | null) => (addition ? addition.trim().split(/\s+/).filter(Boolean).length : 0);

/** The note a Skip records: Skip asks for no reason, the record still says what happened. */
export const SKIP_NOTE = "Skipped in Suggestions.";

/** What the Filter narrows by: free text and the fields an update changes. */
export interface RecordFilter {
  readonly query: string;
  readonly kinds: ReadonlyArray<Recommendation["kind"]>;
}

export const EMPTY_RECORD_FILTER: RecordFilter = { query: "", kinds: [] };

export const activeRecordFilterCount = (filter: RecordFilter) => filter.kinds.length + (filter.query.trim() ? 1 : 0);

/** Every word of the query must appear in the document, the field, a value or the reason. */
export function filterRecordUpdates(updates: ReadonlyArray<RecordUpdate>, filter: RecordFilter): ReadonlyArray<RecordUpdate> {
  const words = filter.query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return updates.filter((update) => {
    if (filter.kinds.length > 0 && !filter.kinds.includes(update.kind)) return false;
    if (words.length === 0) return true;
    const haystack = [update.targetName, update.label, update.before ?? "", update.after, update.why].join(" ").toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
}

export type RecordAction = "apply" | "skip" | "undo";

export interface RecordDecision {
  readonly decision: "apply" | "dismiss" | "reopen" | "undo";
  readonly note?: string;
}

/**
 * The API decision behind a row action, or null when the action does not fit
 * the row. Skip is a dismissal with a fixed note; undoing a Skip reopens; undoing
 * an Apply restores the stored before-value (the API refuses when the value
 * has changed since).
 */
export function recordDecision(action: RecordAction, update: Pick<RecordUpdate, "status" | "legacy">): RecordDecision | null {
  switch (action) {
    case "apply":
      return update.status === "pending" ? { decision: "apply" } : null;
    case "skip":
      return update.status === "pending" ? { decision: "dismiss", note: SKIP_NOTE } : null;
    case "undo":
      if (update.status === "skipped") return { decision: "reopen" };
      return update.status === "applied" && !update.legacy ? { decision: "undo" } : null;
  }
}

/** Undo is offered on a decided row unless it was applied before before-values were kept. */
export const canUndoRecord = (update: Pick<RecordUpdate, "status" | "legacy">) => recordDecision("undo", update) !== null;

export interface RecordOutcome {
  readonly id: string;
  readonly label: string;
  readonly targetName: string;
  /** Null when the decision went through; the API's message otherwise. */
  readonly error: string | null;
}

/**
 * Run one action over many rows, one request at a time (each Apply reads the
 * record the last one wrote), and report each row.
 */
export async function runRecordActions(
  action: RecordAction,
  updates: ReadonlyArray<RecordUpdate>,
  decide: (id: string, decision: RecordDecision) => Promise<string | null>,
): Promise<ReadonlyArray<RecordOutcome>> {
  const outcomes: RecordOutcome[] = [];
  for (const update of updates) {
    const decision = recordDecision(action, update);
    if (!decision) continue;
    const error = await decide(update.id, decision);
    outcomes.push({ id: update.id, label: update.label, targetName: update.targetName, error });
  }
  return outcomes;
}

/** "3 applied", "2 applied · 1 failed": the toast after a batch. */
export function summariseOutcomes(action: RecordAction, outcomes: ReadonlyArray<RecordOutcome>): string {
  const failed = outcomes.filter((outcome) => outcome.error !== null).length;
  const done = outcomes.length - failed;
  const verb = action === "apply" ? "applied" : action === "skip" ? "skipped" : "undone";
  return failed > 0 ? `${done} ${verb} · ${failed} failed` : `${done} ${verb}`;
}
